import env from '#shared/config/env.js';

import levenshtein from './levenshtein.js';
import makeRequest from './makeRequest.js';
import transpositionError from './transpositionError.js';
import validate from './validate.js';

const { default: Tracking } = IS_CLIENT ? await import('#frontend/util/tracking.js') : {};
const tracking = Tracking ? new Tracking() : null;

/**
 * Check for an existence of the domain
 * @param {string} domain
 * @param {object} options Object with email options
 * @param {boolean} [options.checkedDomain]
 * @param {boolean} [options.doesDomainExist]
 * @param {object} cart
 * @returns {Promise}
 */
const checkDomain = (domain, { checkedDomain, doesDomainExist }, cart) => {
	const options = {
		checkedDomain,
		doesDomainExist
	};
	const fetchOptions = {
		json: {
			value: domain
		}
	};
	return new Promise((resolve) => {
		// Ignore domain check for verified domains that are false-negative
		if (env.domainExceptions.includes(domain)) {
			resolve({});
		}
		// Check not yet checked domain
		if (options.checkedDomain !== domain) {
			options.checkedDomain = domain;
			const validationCallTimeout = setTimeout(() => {
				options.doesDomainExist = false;
				resolve(options);
			}, 3500);
			makeRequest.post('/api/v1/cart/validate/email', fetchOptions).then((response) => {
				clearTimeout(validationCallTimeout);
				if (!response.body.data.isValid) {
					tracking.emailTypo(cart, domain);
				}
				options.doesDomainExist = response.body.data.isValid;
				resolve(options);
			}).catch(() => {
				resolve({});
			});
		} else {
			resolve({});
		}
	});
};

/**
 * Get suggestion data
 * @param {string} currentEmail - current customer's email
 * @param {boolean} shouldCheckDomain
 * @param {object} cart
 * @returns {object}
 */
const emailSuggestion = async (currentEmail, shouldCheckDomain = true, cart) => {
	const domainRegEx = /@([^.]*)+(.*)/;
	const options = {
		checkedDomain: '',
		doesDomainExist: true,
		email: currentEmail.toLowerCase(),
		shouldBeGmail: false
	};
	try {
		const [, domain, tld] = domainRegEx.exec(options.email);
		const domainTldInc = `${domain}${tld}`;
		let emailCharDifference;
		let swappedCharacters = false;
		options.originalDomain = domainTldInc;
		if (domain[0] === 'g') {
			emailCharDifference = levenshtein(domain.substring(1), 'mail');
			if (emailCharDifference === 2) {
				swappedCharacters = transpositionError(domain.substring(1), 'mail');
			}
		}
		if (emailCharDifference === 1 || swappedCharacters || emailCharDifference === 0 && tld !== '.com' && (shouldCheckDomain || tld.length > 3)) {
			const email = options.email;
			// Update email (before @ sign) and show suggestion
			options.shouldBeGmail = true;
			options.fixedEmail = {
				email: email.replace(`@${domainTldInc}`, ''),
				domain: 'gmail.com'
			};
			// Hide domain check message
			options.doesDomainExist = false;
			// Track only the first suggestion
			tracking.emailTypo(cart, domainTldInc, options.fixedEmail.domain);
		} else if (domainTldInc && shouldCheckDomain && domainTldInc !== 'gmail.com') {
			const checkDomainResult = await checkDomain(domainTldInc, options, cart);
			Object.assign(options, checkDomainResult);
		}
	} catch {
		// If error, don't show any suggestion/note
	}
	return options;
};

export default async (eventTarget, displayEmailNote = true, checkDomain = true, lastEmail = '', currentEmail = '', suggestions, cart) => {
	const hasError = eventTarget.__vue__.$parent.hasError;
	const isEmailFormatValid = validate.email(currentEmail).isValid;
	if (!hasError && !isEmailFormatValid) {
		return {
			shouldBeGmail: false,
			doesDomainExist: true,
			emailNote: displayEmailNote
		};
	} else if (hasError || !isEmailFormatValid) {
		return {};
	} else {
		let emailSuggestionResult;
		if (lastEmail !== currentEmail) {
			emailSuggestionResult = await emailSuggestion(currentEmail, checkDomain, cart);
		}
		return {
			...suggestions,
			...emailSuggestionResult,
			emailNote: displayEmailNote
		};
	}
};
